import { useEffect } from "react";
import { ACCENT_THEMES, applyAccent, currentAccent, hydrateAccent } from "./accent";
hydrateAccent();

const prefersReduced = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const coarsePointer = () => window.matchMedia("(pointer: coarse)").matches;

// -- scroll reveals: fade/slide in when scrolled into view ------------------
function setupReveals() {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const el = e.target as HTMLElement;
          const d = el.dataset.revealDelay;
          if (d) el.style.transitionDelay = d + "ms";
          el.classList.add("in");
          io.unobserve(el);
        }
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
  );
  const observe = () =>
    document
      .querySelectorAll<HTMLElement>(".reveal:not(.in)")
      .forEach((el) => {
        const d = el.dataset.revealDelay;
        if (d) el.style.transitionDelay = d + "ms";
        io.observe(el);
      });
  observe();
  const mo = new MutationObserver(observe);
  mo.observe(document.body, { childList: true, subtree: true });
  // hard safety net: in some environments (headless, IO throttled or
  // disabled) IntersectionObserver never fires and .reveal would stay
  // opacity:0 forever. Each tick reveals any remaining element that is
  // actually in the viewport (cheap rect check); if IO never fired at
  // all within the hard deadline, reveal everything as a last resort.
  // When IO is healthy this is a no-op (observer marks .in first).
  const started = Date.now();
  const DEADLINE = 3000;
  const inView = (r: DOMRect) => r.bottom > 0 && r.top < window.innerHeight * 1.1;
  const tick = () => {
    document
      .querySelectorAll<HTMLElement>(".reveal:not(.in)")
      .forEach((el) => {
        if (Date.now() - started >= DEADLINE || inView(el.getBoundingClientRect()))
          el.classList.add("in");
      });
  };
  const timer = window.setInterval(tick, 400);
  window.setTimeout(() => window.clearInterval(timer), DEADLINE + 800);
  return () => {
    io.disconnect();
    mo.disconnect();
    window.clearInterval(timer);
  };
}

// -- scramble-decode the page headline on every navigation ------------------
// (joshwcomeau-grade signature: the h1 "decodes" from glyphs into words)
function setupScramble() {
  const GLYPHS = "█▓▒░<>/{}[]#$%&*+=-0123456789";
  const decode = (el: HTMLElement) => {
    const originalHTML = el.dataset.originalHtml || el.innerHTML;
    el.dataset.originalHtml = originalHTML;
    if (prefersReduced()) {
      el.innerHTML = originalHTML;
      return;
    }
    // map each text node to its character range (order = DOM flow), so the
    // reveal sweeps left-to-right even across nested spans/br
    const nodes: Array<{ el: Text; start: number; len: number }> = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let offset = 0,
      n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      const len = t.textContent!.length;
      if (len) nodes.push({ el: t, start: offset, len });
      offset += len;
    }
    const chars = originalHTML.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
    const seed = () => nodes.forEach((nd) => (nd.el.textContent = "█".repeat(nd.len)));
    if (!nodes.length) return;
    seed();
    let frame = 0;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.innerHTML = originalHTML;
    };
    const total = Math.max(24, Math.min(48, chars.length));
    // wall-clock safety: if rAF is throttled (background tab) the heading
    // must never be left as raw glyphs — restore real text on a hard deadline
    const deadline = window.setTimeout(finish, 2600);
    const step = () => {
      if (done) return;
      frame++;
      const target = Math.floor((frame / total) * chars.length);
      nodes.forEach((nd) => {
        const before = Math.max(0, Math.min(nd.len, target - nd.start));
        const after = nd.len - before;
        let s = "";
        for (let k = 0; k < before; k++) s += chars[nd.start + k];
        for (let k = 0; k < after; k++)
          s += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        nd.el.textContent = s;
      });
      if (frame < total) requestAnimationFrame(step);
      else {
        clearTimeout(deadline);
        finish();
      }
    };
    requestAnimationFrame(step);
  };
  const run = () => {
    // the page headline only — the home hero keeps its own word-stagger
    const h1 = document.querySelector<HTMLElement>(".p-head-h1, .a-h1, .journal-h1");
    if (h1) decode(h1);
  };
  // hash router → hashchange fires on every route; also fire once on load
  window.addEventListener("hashchange", run);
  const t = window.setTimeout(run, 120);
  return () => {
    window.removeEventListener("hashchange", run);
    window.clearTimeout(t);
  };
}

// -- count-up stats: animate .stat-num to its data-target on reveal ---------
function setupCountUp() {
  const animate = (el: HTMLElement) => {
    const target = parseFloat(el.dataset.target || "0");
    const suffix = el.dataset.suffix || "";
    const dur = 1400;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = target + suffix;
    };
    if (prefersReduced()) {
      el.textContent = target + suffix;
      return;
    }
    requestAnimationFrame(tick);
  };
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          animate(e.target as HTMLElement);
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.4 }
  );
  const scan = () =>
    document
      .querySelectorAll<HTMLElement>(".stat-num[data-target]:not(.counted)")
      .forEach((el) => {
        el.classList.add("counted");
        io.observe(el);
      });
  scan();
  const mo = new MutationObserver(scan);
  mo.observe(document.body, { childList: true, subtree: true });
  return () => {
    io.disconnect();
    mo.disconnect();
  };
}

// -- cursor spotlight: soft light follows the mouse over .spot cards --------
function setupSpotlight() {
  if (coarsePointer()) return () => {};
  const setVar = (el: HTMLElement, e: MouseEvent) => {
    const r = el.getBoundingClientRect();
    el.style.setProperty("--sx", e.clientX - r.left + "px");
    el.style.setProperty("--sy", e.clientY - r.top + "px");
  };
  const onMove = (e: MouseEvent) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(".spot");
    if (t) setVar(t, e);
  };
  document.addEventListener("mousemove", onMove, { passive: true });
  return () => document.removeEventListener("mousemove", onMove);
}

// -- magnetic CTA: .magnet buttons pull gently toward the cursor -----------
function setupMagnetic() {
  if (coarsePointer()) return () => {};
  const strength = 0.28;
  const onMove = (e: MouseEvent) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(".magnet");
    if (!t) return;
    const r = t.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    t.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
  };
  const onOut = (e: MouseEvent) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(".magnet");
    if (!t) return;
    // only clear when actually leaving the magnet (not a child hop)
    const to = (e.relatedTarget as Node | null) || null;
    if (to && t.contains(to)) return;
    t.style.transform = "";
  };
  document.addEventListener("mousemove", onMove, { passive: true });
  document.addEventListener("mouseout", onOut, { passive: true });
  return () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseout", onOut);
  };
}

// -- film grain: fixed overlay that gives the dark bg real texture ----------
function setupGrain() {
  const g = document.createElement("div");
  g.className = "love-grain";
  g.setAttribute("aria-hidden", "true");
  document.body.appendChild(g);
  return () => g.remove();
}

// -- custom cursor: dot + ring that chases the mouse -------------------------
function setupCursor() {
  if (coarsePointer()) return () => {};
  const dot = document.createElement("div");
  dot.className = "love-cursor-dot";
  const ring = document.createElement("div");
  ring.className = "love-cursor-ring";
  document.body.appendChild(dot);
  document.body.appendChild(ring);
  let mx = -100, my = -100, rx = -100, ry = -100;
  const onMove = (e: MouseEvent) => {
    mx = e.clientX;
    my = e.clientY;
    dot.style.transform = `translate(${mx}px, ${my}px)`;
    const t = e.target as HTMLElement;
    const hot = !!(
      t.closest("a, button, .tilt, .zc-btn, .chip, .work-row, .p-feat-card, .p-tile")
    );
    ring.classList.toggle("hot", hot);
  };
  let raf = 0;
  const loop = () => {
    rx += (mx - rx) * 0.18;
    ry += (my - ry) * 0.18;
    ring.style.transform = `translate(${rx}px, ${ry}px)`;
    raf = requestAnimationFrame(loop);
  };
  window.addEventListener("mousemove", onMove);
  raf = requestAnimationFrame(loop);
  return () => {
    window.removeEventListener("mousemove", onMove);
    cancelAnimationFrame(raf);
    dot.remove();
    ring.remove();
  };
}

// -- 3D tilt on .tilt elements (dynamic — picks up route mounts) ------------
function setupTilt() {
  if (coarsePointer()) return () => {};
  const attached = new WeakSet<HTMLElement>();
  const cleanups = new Map<HTMLElement, Array<() => void>>();
  const attach = (el: HTMLElement) => {
    if (attached.has(el)) return;
    attached.add(el);
    const max = 7;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(900px) rotateY(${px * max}deg) rotateX(${-py * max}deg) translate(-2px, -2px)`;
      el.style.boxShadow = `${8 + px * 6}px ${8 + py * 6}px 0 0 var(--accent)`;
    };
    const onLeave = () => {
      el.style.transform = "";
      el.style.boxShadow = "";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    cleanups.set(el, [
      () => el.removeEventListener("mousemove", onMove),
      () => el.removeEventListener("mouseleave", onLeave),
    ]);
  };
  const scan = () =>
    document.querySelectorAll<HTMLElement>(".tilt").forEach(attach);
  scan();
  const mo = new MutationObserver(scan);
  mo.observe(document.body, { childList: true, subtree: true });
  return () => {
    mo.disconnect();
    cleanups.forEach((fns) => fns.forEach((f) => f()));
    cleanups.clear();
  };
}

// -- konami party mode --------------------------------------------------------
function setupKonami() {
  const seq = [
    "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
    "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a",
  ];
  let i = 0;
  const onKey = (e: KeyboardEvent) => {
    if (e.key === seq[i]) {
      i++;
      if (i === seq.length) {
        i = 0;
        document.body.classList.add("party");
        const toast = document.createElement("div");
        toast.className = "party-toast";
        toast.textContent = "party mode unlocked 🎉";
        document.body.appendChild(toast);
        setTimeout(() => document.body.classList.remove("party"), 3000);
        setTimeout(() => toast.remove(), 3200);
      }
    } else if (e.key === seq[0]) {
      i = 1;
    } else {
      i = 0;
    }
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}

// -- double-click a headline: emoji burst ------------------------------------
function setupBurst() {
  const EMOJI = ["🕸️", "🚀", "🛠️", "🍺", "🎮", "✨"];
  const burst = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      !target.closest(
        ".hero-h1, .a-h1, .p-head-h1, .a-belief-h2, .a-cta-h2, .p-cta-h2, .journal-h1"
      )
    )
      return;
    for (let n = 0; n < 14; n++) {
      const s = document.createElement("span");
      s.textContent = EMOJI[Math.floor(Math.random() * EMOJI.length)];
      const dx = (Math.random() - 0.5) * 280;
      const dy = -(80 + Math.random() * 180);
      Object.assign(s.style, {
        position: "fixed",
        left: `${e.clientX}px`,
        top: `${e.clientY}px`,
        zIndex: "9998",
        pointerEvents: "none",
        fontSize: "16px",
        transform: "translate(0,0)",
        transition: "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.9s ease",
      });
      document.body.appendChild(s);
      requestAnimationFrame(() => {
        s.style.transform = `translate(${dx}px, ${dy + 220}px) rotate(${
          (Math.random() - 0.5) * 360
        }deg)`;
        s.style.opacity = "0";
      });
      setTimeout(() => s.remove(), 1000);
    }
  };
  document.addEventListener("dblclick", burst);
  return () => document.removeEventListener("dblclick", burst);
}

// -- route wipe: a colored panel sweeps across on every navigation ---------
function setupRouteWipe() {
  if (prefersReduced()) return () => {};
  const wipe = document.createElement("div");
  wipe.className = "route-wipe";
  wipe.innerHTML =
    '<span class="route-wipe-bar"></span><span class="route-wipe-label">zac.arlin</span>';
  document.body.appendChild(wipe);
  let timer: number;
  const play = () => {
    wipe.classList.remove("play");
    // force reflow so the animation restarts
    void wipe.offsetWidth;
    wipe.classList.add("play");
    window.clearTimeout(timer);
    timer = window.setTimeout(() => wipe.classList.remove("play"), 900);
  };
  window.addEventListener("hashchange", play);
  return () => {
    window.removeEventListener("hashchange", play);
    window.clearTimeout(timer);
    wipe.remove();
  };
}

// -- hero terminal: live typing shell — observer-based so it survives
//    Home being lazy-loaded (the element only exists after the route mounts)
function setupHeroTerminal() {
  const lines = [
    "deploy --net ipfs --tag zacarlin.eth --replicas 3",
    "git push gitea donna/site-redesign — done ✓",
    "qmd search 'ipfs uncensorable' — 3 hits",
  ];
  let timer = 0;
  let started = false;

  const start = (el: HTMLElement) => {
    if (started) return;
    started = true;
    el.textContent = el.dataset.type || lines[0];
    if (prefersReduced()) return;
    let li = 0,
      ci = 0,
      deleting = false;
    const tick = () => {
      const line = lines[li];
      if (!deleting) {
        ci++;
        if (ci >= line.length) {
          el.textContent = line;
          deleting = true;
          timer = window.setTimeout(tick, 1500);
          return;
        }
        el.textContent = line.slice(0, ci);
        timer = window.setTimeout(tick, 26 + Math.random() * 40);
      } else {
        ci -= 2;
        if (ci <= 0) {
          ci = 0;
          deleting = false;
          li = (li + 1) % lines.length;
          el.textContent = "";
          timer = window.setTimeout(tick, 320);
          return;
        }
        el.textContent = line.slice(0, ci);
        timer = window.setTimeout(tick, 14);
      }
    };
    el.textContent = "";
    timer = window.setTimeout(tick, 700);
  };

  // find it now if it's there
  const existing = document.querySelector<HTMLElement>(".hero-terminal-cmd");
  if (existing) {
    start(existing);
    return () => window.clearTimeout(timer);
  }

  // otherwise wait for Home to mount
  const mo = new MutationObserver(() => {
    const found = document.querySelector<HTMLElement>(".hero-terminal-cmd");
    if (found) {
      mo.disconnect();
      start(found);
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  return () => {
    mo.disconnect();
    window.clearTimeout(timer);
  };
}

// -- shared smooth-scroll helper ---------------------------------------------
// Smooth where supported; if it hasn't reached the target after a short delay
// (some browsers / headless contexts silently drop smooth scroll), force an
// instant jump so the button always does something. "instant" is the reliable
// keyword — "auto" inherits the site's global scroll-behavior:smooth.
function scrollToEl(el: HTMLElement) {
  const top = el.getBoundingClientRect().top + window.scrollY - 8;
  el.scrollIntoView({ behavior: prefersReduced() ? "auto" : "smooth", block: "start" });
  window.setTimeout(() => {
    if (Math.abs(window.scrollY - top) > 48) {
      const rootEl = document.documentElement;
      const prev = rootEl.style.scrollBehavior;
      rootEl.style.scrollBehavior = "auto";
      el.scrollIntoView({ behavior: "auto", block: "start" });
      window.scrollTo(0, top);
      rootEl.style.scrollBehavior = prev;
    }
  }, 520);
}



// -- scroll hint: "explore ↓" pill — scrolls to #more, hides after scroll --
function setupScrollHint() {
  const attach = (hint: HTMLElement) => {
    const target = () => document.getElementById("more");
    const onClick = () => {
      const el = target();
      if (!el) return;
      scrollToEl(el);
    };
    hint.addEventListener("click", onClick);
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.max(window.scrollY, 0);
        hint.classList.toggle("is-hidden", y > 60);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      hint.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  };
  let hint = document.querySelector<HTMLElement>(".hero-scrollhint");
  if (hint) return attach(hint);
  // Home may not be mounted yet (lazy route). Watch for it.
  let clean: (() => void) | null = null;
  const mo = new MutationObserver(() => {
    const el = document.querySelector<HTMLElement>(".hero-scrollhint");
    if (el) {
      mo.disconnect();
      clean = attach(el);
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  return () => {
    mo.disconnect();
    clean && clean();
  };
}

// -- scroll-to-top: a square button fades in after you've gone deep --------
function setupScrollTop() {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "love-scrolltop";
  btn.setAttribute("aria-label", "Back to top");
  btn.innerHTML = "&#8593;";
  document.body.appendChild(btn);
  const onScroll = () => {
    const y = Math.max(window.scrollY, 0);
    btn.classList.toggle("show", y > 600);
  };
  const onClick = () => {
    const top = document.scrollingElement || document.documentElement;
    const behavior: ScrollBehavior = prefersReduced() ? "auto" : "smooth";
    (top as HTMLElement).scrollTo({ top: 0, behavior });
    // belt-and-suspenders: if the window didn't move (flex scroll quirk),
    // force it via the body's parent.
    window.setTimeout(() => {
      if (window.scrollY > 24) window.scrollTo(0, 0);
    }, 120);
  };
  btn.addEventListener("click", onClick);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => {
    btn.removeEventListener("click", onClick);
    window.removeEventListener("scroll", onScroll);
    btn.remove();
  };
}

// -- scroll progress: a thin accent bar across the very top -----------------
function setupScrollProgress() {
  const bar = document.createElement("div");
  bar.className = "love-scroll-progress";
  bar.setAttribute("aria-hidden", "true");
  document.body.appendChild(bar);
  let raf = 0;
  const onScroll = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const p = max > 0 ? h.scrollTop / max : 0;
      bar.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  onScroll();
  return () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    cancelAnimationFrame(raf);
    bar.remove();
  };
}

// -- hero parallax: the grid floor drifts as you scroll ---------------------
function setupHeroParallax() {
  if (prefersReduced() || coarsePointer()) return () => {};
  let raf = 0;
  const onScroll = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const hero = document.querySelector<HTMLElement>(".hero");
      if (!hero) return;
      const r = hero.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      // 0 at top-of-viewport, grows as hero scrolls out — floor slides toward viewer
      const progress = Math.min(1, Math.max(0, -r.top / Math.max(1, r.height)));
      hero.style.setProperty("--hero-y", `${progress * 90}px`);
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => {
    window.removeEventListener("scroll", onScroll);
    cancelAnimationFrame(raf);
  };
}

// -- "e" easter egg: party mode unlock (e = party) ---------------------------

// -- love: footer heart, double-click hearts, "send some love" -------------
const LOVE_LINES = [
  "love received ♥ now go say hi 📬",
  "you already do. that's the whole point.",
  "noted — the site is very loved.",
  "same ♥",
];
const HEARTS_KEY = "z4c-hearts";
// the site gets warmer the more you send — tiers it climbs, in its voice
const LOVE_TIERS = [
  { at: 1, label: "just met you" },
  { at: 10, label: "a little smitten" },
  { at: 25, label: "head over heels" },
  { at: 50, label: "yours, officially" },
  { at: 100, label: "the site has a crush on you now" },
];
const loveTier = (n: number) => {
  let tier: { at: number; label: string } | null = null;
  for (const t of LOVE_TIERS) if (n >= t.at) tier = t;
  return tier;
};
const loveToast = (msg: string, ms = 2400) => {
  const t = document.createElement("div");
  t.className = "party-toast";
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), ms);
};

export function sendLove(
  x?: number,
  y?: number,
  opts?: { toast?: boolean; count?: number }
) {
  const cx = x ?? window.innerWidth / 2;
  const cy = y ?? window.innerHeight / 2;
  const burst = (n: number) => {
    if (prefersReduced()) return;
    for (let i = 0; i < n; i++) {
      const s = document.createElement("span");
      s.className = "love-heart";
      s.textContent = "♥";
      s.style.left = cx + "px";
      s.style.top = cy + "px";
      const ang = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 110;
      s.style.setProperty("--hx", Math.cos(ang) * dist + "px");
      s.style.setProperty("--hy", Math.sin(ang) * dist - 50 + "px");
      s.style.setProperty("--hr", (Math.random() * 90 - 45) + "deg");
      s.style.animationDelay = Math.random() * 90 + "ms";
      document.body.appendChild(s);
      setTimeout(() => s.remove(), 1100);
    }
  };
  // tally — the site remembers its love and gets warmer as you climb tiers
  let total = 0;
  try {
    total = (parseInt(localStorage.getItem(HEARTS_KEY) || "0", 10) || 0) + 1;
    localStorage.setItem(HEARTS_KEY, String(total));
  } catch {
    /* private mode — love still lands, it just goes uncounted */
  }
  burst(opts?.count ?? 14);
  window.dispatchEvent(new CustomEvent("z4c:hearts"));
  // crossed a new tier? that's the real milestone — bigger burst + a line
  const tier = loveTier(total);
  if (tier && total >= tier.at && (total - 1) < tier.at) {
    burst(30);
    loveToast(`♥ ${tier.label} — ${total} hearts and counting`, 3200);
    return;
  }
  if (opts?.toast !== false) {
    loveToast(LOVE_LINES[(Math.random() * LOVE_LINES.length) | 0]);
  }
}

export function heartsSent(): number {
  try {
    return parseInt(localStorage.getItem(HEARTS_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function setupLovePulse() {
  const tally = () => {
    const el = document.querySelector<HTMLElement>(".f-love-tally");
    if (!el) return;
    const n = heartsSent();
    const tier = loveTier(n);
    el.textContent = n ? ` · ${n} ♥${tier ? " · " + tier.label : ""}` : "";
  };
  const scan = () => {
    document.querySelectorAll<HTMLElement>(".f-love").forEach((el) => {
      if (el.dataset.love) return;
      el.dataset.love = "1";
      const fire = (clientX?: number, clientY?: number) => {
        const r = el.getBoundingClientRect();
        sendLove(clientX ?? r.left + r.width / 2, clientY ?? r.top + r.height / 2);
      };
      el.addEventListener("click", () => fire());
      el.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          fire();
        }
      });
    });
    tally();
  };
  scan();
  const mo = new MutationObserver(scan);
  mo.observe(document.body, { childList: true, subtree: true });
  window.addEventListener("z4c:hearts", tally);
  // one quiet heart on a double-click — anywhere except things that
  // already celebrate (headlines have their own bigger burst)
  const onDbl = (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    if (!t) return;
    if (t.closest("input, textarea, select, button, a, .tilt, .zc-btn, .work-row, .p-feat-card")) return;
    if (t.closest(".hero-h1, .a-h1, .p-head-h1, .a-belief-h2, .a-cta-h2, .p-cta-h2, .journal-h1")) return;
    sendLove(e.clientX, e.clientY, { toast: false, count: 1 });
  };
  document.addEventListener("dblclick", onDbl);
  return () => {
    mo.disconnect();
    window.removeEventListener("z4c:hearts", tally);
    document.removeEventListener("dblclick", onDbl);
  };
}

function setupPartyKey() {
  const burst = (x: number, y: number, count: number) => {
    if (prefersReduced()) return;
    const colors = ["#ff5c00", "#ffe813", "#2b4bff", "#ff4fa3", "#00e5a0"];
    for (let i = 0; i < count; i++) {
      const p = document.createElement("i");
      p.className = "confetti";
      const ang = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 180;
      const size = 6 + Math.random() * 8;
      p.style.left = x + "px";
      p.style.top = y + "px";
      p.style.width = size + "px";
      p.style.height = size + "px";
      p.style.background = colors[(Math.random() * colors.length) | 0];
      p.style.setProperty("--cx", Math.cos(ang) * dist + "px");
      p.style.setProperty("--cy", Math.sin(ang) * dist - 120 + "px");
      p.style.setProperty("--rot", (Math.random() * 720 - 360) + "deg");
      p.style.animationDelay = Math.random() * 80 + "ms";
      document.body.appendChild(p);
      setTimeout(() => p.remove(), 1400);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "e" && e.key !== "E") return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target as HTMLElement;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable))
      return;
    document.body.classList.add("party");
    burst(window.innerWidth / 2, window.innerHeight / 2, 40);
    const toast = document.createElement("div");
    toast.className = "party-toast";
    toast.textContent = "party mode unlocked 🎉";
    document.body.appendChild(toast);
    setTimeout(() => document.body.classList.remove("party"), 4000);
    setTimeout(() => toast.remove(), 4200);
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}


// -- hero photo depth: stickers drift on a z-parallax with the cursor -----
export function setupPhotoDepth(root: ParentNode = document) {
  if (prefersReduced() || coarsePointer()) return () => {};
  const hero = root.querySelector(".hero-photo");
  if (!hero) return () => {};
  const badge = hero.querySelector(".photo-badge");
  const tag = hero.querySelector(".photo-tag");
  // frame is a .tilt element — tilt owns its transform, so we only float the stickers
  const layers: Array<{ el: HTMLElement; fx: number; fy: number }> = [
    { el: badge as HTMLElement, fx: 14, fy: 10 },
    { el: tag as HTMLElement, fx: -10, fy: -8 },
  ].filter((l) => l.el);
  let raf = 0;
  const onMove = (e: MouseEvent) => {
    const r = hero.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      layers.forEach((l) => {
        l.el.style.transition = "none"; // 1:1 cursor follow, no lag
        l.el.style.transform = `translate(${nx * l.fx}px, ${ny * l.fy}px)`;
      });
    });
  };
  const onLeave = () => {
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    layers.forEach((l) => {
      l.el.style.transition = "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
      l.el.style.transform = "";
    });
  };
  hero.addEventListener("mousemove", onMove, { passive: true });
  hero.addEventListener("mouseleave", onLeave);
  return () => {
    hero.removeEventListener("mousemove", onMove);
    hero.removeEventListener("mouseleave", onLeave);
    if (raf) cancelAnimationFrame(raf);
  };
}
export function setupLiveClock(root: ParentNode = document) {
  const els = Array.prototype.slice.call(
    root.querySelectorAll<HTMLElement>(".live-clock")
  );
  if (!els.length) return () => {};
  const render = () => {
    els.forEach((el) => {
      const tz = el.dataset.tz || "local";
      try {
        el.textContent = new Date().toLocaleTimeString(
          undefined,
          {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: tz === "local" ? undefined : tz,
          }
        );
      } catch (_e) {
        el.textContent = "";
      }
    });
  };
  render();
  const id = window.setInterval(render, 1000);
  return () => window.clearInterval(id);
}


// -- copy email to clipboard + toast (attached per-element, not global) ----
// call: attachCopyEmail(buttonEl, "zacarlin@gmail.com")
// -- accent dock: 4 swatches, bottom-left, re-skins the whole site live ----
function setupAccentDock() {
  const dock = document.createElement("div");
  dock.className = "accent-dock";
  dock.setAttribute("role", "radiogroup");
  dock.setAttribute("aria-label", "Accent colour");
  const label = document.createElement("span");
  label.className = "accent-dock-label mono";
  label.textContent = "accent";
  dock.appendChild(label);

  // Collapsed pill at EVERY width (not just mobile) so the dock never sits on
  // top of body text by default. Tap the pill → expand; tap a swatch → select + re-collapse.
  const collapseDefault = () => { dock.classList.add("is-collapsed"); };
  collapseDefault();

  ACCENT_THEMES.forEach((t) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "accent-swatch";
    b.style.background = t.accent;
    b.title = t.label + " accent";
    b.setAttribute("role", "radio");
    b.setAttribute("aria-label", "Switch accent to " + t.label);
    b.dataset.accentId = t.id;
    b.setAttribute("aria-checked", "false");
    b.addEventListener("click", () => {
      const chosen = nextAccentFrom(t.id);
      syncDock(chosen.id);
      const toast = document.createElement("div");
      toast.className = "party-toast";
      toast.textContent = "accent → " + chosen.label + " ✦";
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 1500);
      collapseDefault();
    });
    dock.appendChild(b);
  });
  const syncDock = (id: string) => {
    dock.querySelectorAll<HTMLElement>(".accent-swatch").forEach((b) => {
      const on = b.dataset.accentId === id;
      b.setAttribute("aria-checked", on ? "true" : "false");
      b.classList.toggle("is-active", on);
    });
  };
  const init = currentAccent().id;
  applyAccent(init);
  syncDock(init);

  // collapsed pill: any tap expands it (capture phase, so it wins over the
  // active-swatch's own click handler); open state lets swatch clicks select.
  dock.addEventListener(
    "click",
    (e) => {
      if (!dock.classList.contains("is-collapsed")) return;
      dock.classList.remove("is-collapsed");
      e.stopPropagation();
    },
    true
  );
  const onDocClick = (e: Event) => {
    if (dock.contains(e.target as Node)) return;
    collapseDefault();
  };
  document.addEventListener("click", onDocClick);

  document.body.appendChild(dock);
  return () => {
    document.removeEventListener("click", onDocClick);
    dock.remove();
  };
}

export function nextAccentFrom(id: string) {
  const t = ACCENT_THEMES.find((x) => x.id === id) || ACCENT_THEMES[0];
  applyAccent(t.id);
  try { localStorage.setItem("z4c-accent", t.id); } catch (_e) { /* noop */ }
  return t;
}

// -- hero H1 accent hover — a quick re-flash on the highlighted words --------
// Pure CSS can't do it (the .hl spans already carry their own bg/color/border),
// so we add a keyframe + a hover listener that toggles a class. Feels alive
// without fighting the entrance animation.
export function setupHeroAccentHover(root: ParentNode = document) {
  const h1 = root.querySelector(".hero-h1");
  if (!h1) return () => {};
  const acc = Array.prototype.slice.call(h1.querySelectorAll<HTMLElement>(".hl, .hl2"));
  if (!acc.length) return () => {};
  // keyframe injected once (idempotent)
  if (!document.getElementById("hero-acc-kf")) {
    const st = document.createElement("style");
    st.id = "hero-acc-kf";
    st.textContent =
      "@keyframes hero-acc-pulse{0%{filter:brightness(1)}40%{filter:brightness(1.5) saturate(1.3)}100%{filter:brightness(1)}}" +
      ".hero-acc-flash{animation:hero-acc-pulse .45s ease}";
    document.head.appendChild(st);
  }
  let raf = 0;
  const onMove = (e: MouseEvent) => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const t = (e.target as HTMLElement).closest<HTMLElement>(".hl, .hl2");
      if (t && !t.classList.contains("hero-acc-flash")) {
        t.classList.add("hero-acc-flash");
        const done = () => t.classList.remove("hero-acc-flash");
        t.addEventListener("animationend", done, { once: true });
        // safety: drop the class if animationend never fires (reduced-motion)
        setTimeout(done, 600);
      }
    });
  };
  h1.addEventListener("mousemove", onMove, { passive: true });
  return () => h1.removeEventListener("mousemove", onMove);
}

// -- single-key navigation: H home, P projects, A about, J journal ----------
// Only fires when focus isn't in a form control and the ⌘K palette is closed.
function setupNavKeys() {
  const nav = { h: "#/", p: "#/projects", a: "#/about", j: "#/journal" };
  const labels = { h: "home", p: "projects", a: "about", j: "journal" };
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = document.activeElement as HTMLElement;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    const palette = document.querySelector(".cmdk") as HTMLElement | null;
    if (palette && !palette.hidden) return; // palette open — let it own the keys
    const k = e.key.toLowerCase();
    if (!nav[k]) return;
    e.preventDefault();
    if (window.location.hash !== nav[k]) {
      window.location.hash = nav[k];
    }
    // toast feedback
    const toast = document.createElement("div");
    toast.className = "party-toast";
    toast.textContent = "→ " + labels[k];
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 900);
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}

// -- "?" shortcuts cheat-sheet — surfaces all the hidden easter eggs --------
function setupHelp() {
  const el = document.createElement("div");
  el.className = "help-card";
  el.hidden = true;
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Keyboard shortcuts and easter eggs");
  el.innerHTML =
    '<div class="help-head">' +
    '<span class="help-title mono">cheat sheet</span>' +
    '<button class="help-close mono" aria-label="Close">×</button>' +
    '</div>' +
    '<ul class="help-list mono">' +
    '<li><kbd>⌘</kbd><kbd>K</kbd><span>command palette</span></li>' +
    '<li><kbd>H</kbd><kbd>P</kbd><kbd>A</kbd><kbd>J</kbd><span>jump to home · projects · about · journal</span></li>' +
    '<li><kbd>E</kbd><span>party mode (confetti)</span></li>' +
    '<li><span class="help-conami">↑↑↓↓←→←→BA</span><span>Konami code → party mode</span></li>' +
    '<li><span class="help-accent">swatch dock</span><span>re-skin the whole site</span></li>' +
    '<li><span>any key on 404</span><span>send you home</span></li>' +
    '<li><span>♥ (the heart in the footer)</span><span>the site remembers — love builds</span></li>' +
    '<li><span>dblclick anywhere</span><span>a quiet heart appears</span></li>' +
    '</ul>' +
    '<p class="help-foot mono">click anywhere outside to close</p>';
  document.body.appendChild(el);
  const closeBtn = el.querySelector(".help-close") as HTMLButtonElement;
  const close = () => { el.hidden = true; };
  closeBtn.addEventListener("click", close);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
    if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
      const t = document.activeElement as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      el.hidden = false;
      closeBtn.focus();
    }
  };
  window.addEventListener("keydown", onKey);
  return () => { window.removeEventListener("keydown", onKey); el.remove(); };
}

function setupCopyCode() {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        wire(e.target as HTMLElement);
        io.unobserve(e.target);
      }
    });
  }, { rootMargin: "120px" });
  const wire = (pre: HTMLElement) => {
    if (pre.querySelector(".copy-code")) return;
    const code = (pre.querySelector("code") || pre).textContent || "";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "copy-code";
    btn.setAttribute("aria-label", "Copy code");
    btn.innerHTML =
      '<span class="copy-code-icon">⧉</span><span class="copy-code-label mono">copy</span>';
    const doCopy = () => {
      const done = (ok: boolean) => {
        btn.classList.add(ok ? "is-copied" : "is-err");
        const lbl = btn.querySelector(".copy-code-label");
        if (lbl) lbl.textContent = ok ? "copied" : "err";
        setTimeout(() => {
          btn.classList.remove("is-copied", "is-err");
          if (lbl) lbl.textContent = "copy";
        }, 1400);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(() => done(true), () => done(false));
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); done(true); }
        catch (_e) { done(false); }
        ta.remove();
      }
    };
    btn.addEventListener("click", doCopy);
    pre.appendChild(btn);
  };
  const scan = () =>
    document.querySelectorAll<HTMLElement>("pre").forEach((pre) => io.observe(pre));
  scan();
  const mo = new MutationObserver(scan);
  mo.observe(document.body, { childList: true, subtree: true });
  return () => { io.disconnect(); mo.disconnect(); };
}

// -- command palette (⌘K / Ctrl+K) — routes, posts, actions ------------------
// exported so App can pass in the socks-librarian getArticles (not importable
// from here) — the palette lazy-loads posts on first open.
export function setupCmdPalette(
  getArticles: (opts: any) => Promise<any[]>
) {
  const root = document.createElement("div");
  root.className = "cmdk";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", "Command palette");
  root.hidden = true;
  root.innerHTML = `
    <div class="cmdk-backdrop"></div>
    <div class="cmdk-panel">
      <div class="cmdk-head">
        <span class="cmdk-glyph mono" aria-hidden="true">⌘</span>
        <input class="cmdk-input" type="text" placeholder="Jump to a post, page, or run a command…" autocomplete="off" spellcheck="false" />
        <kbd class="cmdk-esc mono" aria-hidden="true">esc</kbd>
      </div>
      <ul class="cmdk-list" role="listbox"></ul>
      <div class="cmdk-foot mono">
        <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span><kbd>↵</kbd> open</span>
        <span>zac · interwebs</span>
      </div>
    </div>`;
  const backdrop = root.querySelector<HTMLElement>(".cmdk-backdrop");
  const input = root.querySelector<HTMLInputElement>(".cmdk-input");
  const ul = root.querySelector<HTMLElement>(".cmdk-list");

  interface Item {
    id: string;
    label: string;
    hint?: string;
    group: string;
    run: () => void;
  }

  const actions: Item[] = [
    { id: "contact", label: "Open the contact form (say hi)", group: "Action", run: () => { const el = document.querySelector<HTMLElement>(".contact-link"); if (el) el.click(); else { window.location.hash = "#/about"; } } },
    { id: "party", label: "Unlock party mode", group: "Action", run: () => window.dispatchEvent(new KeyboardEvent("keydown", { key: "e", bubbles: true })) },
    { id: "copy-email", label: "Copy zacarlin@gmail.com", group: "Action", run: () => { const el = document.querySelector<HTMLElement>(".f-copy"); if (el) el.click(); else if (navigator.clipboard) navigator.clipboard.writeText("zacarlin@gmail.com").catch(() => {}); } },
    { id: "accent-next", label: "Cycle to next accent theme", group: "Action", run: () => { const sw = Array.from(document.querySelectorAll<HTMLElement>(".accent-swatch")); if (sw.length) { const i = sw.findIndex((x) => x.classList.contains("is-active")); (sw[(i + 1 + sw.length) % sw.length]).click(); } } },
    { id: "love", label: "Send the site some love ♥", group: "Action", run: () => sendLove() },
    { id: "love-status", label: "How much does the site love you?", group: "Action", run: () => { const n = heartsSent(); const t = loveTier(n); if (!n) { loveToast("the site is patient. it's waiting for the first ♥"); return; } loveToast(`♥ ${t ? t.label : "just getting started"} — ${n} hearts`, 3000); } },
    { id: "top", label: "Back to top", group: "Action", run: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
    { id: "ipfs", label: "Open the .eth copy ( zacarlin.eth )", group: "Action", run: () => window.open("https://zacarlin.eth.limo/", "_blank", "noopener") },
  ];
  const routes: Item[] = [
    { id: "home", label: "Home — I build the things that run on the web", group: "Go to", run: () => { window.location.hash = "#/"; } },
    { id: "projects", label: "Projects — the work I'd defend", group: "Go to", run: () => { window.location.hash = "#/projects"; } },
    { id: "about", label: "About — the person behind the commits", group: "Go to", run: () => { window.location.hash = "#/about"; } },
    { id: "journal", label: "Journal — the receipts", group: "Go to", run: () => { window.location.hash = "#/journal"; } },
  ];

  const groups = ["Posts", "Go to", "Action"];
  let posts: Item[] = [];
  let postsState: "pending" | "done" | "err" = "pending";

  async function loadPosts() {
    if (postsState !== "pending") return;
    postsState = "done";
    try {
      const data = (await getArticles({ user: "About7Sharks", repo: "Markdown" })) || [];
      posts = data.map((a: any) => ({
        id: "post-" + (a.id || a.title),
        label: a.title || "untitled",
        hint: (a.date || "").toString().slice(0, 10),
        group: "Posts",
        run: () => { window.location.hash = "#/journal/" + encodeURIComponent(a.id || a.title); },
      }));
      render(filter(input.value || ""));
    } catch (_e) { postsState = "err"; }
  }

  const filter = (q: string) => {
    const s = (q || "").toLowerCase().trim();
    return routes
      .concat(posts)
      .concat(actions)
      .filter((it) => !s || it.label.toLowerCase().includes(s) || (it.hint || "").toLowerCase().includes(s) || it.group.toLowerCase().includes(s))
      .sort((a, b) => groups.indexOf(a.group) - groups.indexOf(b.group) || a.label.localeCompare(b.label));
  };

  let sel = 0;
  let items: Item[] = [];

  const render = (arr: Item[]) => {
    items = arr;
    sel = 0;
    ul.innerHTML = "";
    let lastGroup = "";
    arr.forEach((it, i) => {
      if (it.group !== lastGroup) {
        lastGroup = it.group;
        const g = document.createElement("li");
        g.className = "cmdk-group mono";
        g.textContent = it.group;
        ul.appendChild(g);
      }
      const li = document.createElement("li");
      li.className = "cmdk-item" + (i === sel ? " is-sel" : "");
      li.setAttribute("role", "option");
      const label = document.createElement("span");
      label.className = "cmdk-item-label";
      label.textContent = it.label;
      li.appendChild(label);
      if (it.hint) {
        const h = document.createElement("span");
        h.className = "cmdk-item-hint mono";
        h.textContent = it.hint;
        li.appendChild(h);
      }
      li.addEventListener("mousedown", (e) => { e.preventDefault(); pick(it); });
      ul.appendChild(li);
    });
  };
  const paint = () => {
    const lis = Array.from(ul.querySelectorAll<HTMLElement>(".cmdk-item"));
    lis.forEach((li, i) => li.classList.toggle("is-sel", i === sel));
    if (lis[sel]) lis[sel].scrollIntoView({ block: "nearest" });
  };
  const close = () => { root.hidden = true; input.value = ""; setTimeout(() => input.blur(), 0); };
  const open = () => {
    root.hidden = false;
    loadPosts();
    render(filter(input.value || ""));
    paint();
    setTimeout(() => input.focus(), 10);
  };
  const pick = (it: Item) => { close(); setTimeout(() => it.run(), 30); };
  const onKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      if (root.hidden) open(); else close();
      return;
    }
    if (root.hidden) return;
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); sel = Math.min(items.length - 1, sel + 1); paint(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); sel = Math.max(0, sel - 1); paint(); }
    else if (e.key === "Enter") { e.preventDefault(); const it = items[sel]; if (it) pick(it); }
  };
  input.addEventListener("input", () => { render(filter(input.value || "")); paint(); });
  backdrop.addEventListener("click", close);
  window.addEventListener("keydown", onKey);
  document.body.appendChild(root);
  return () => { window.removeEventListener("keydown", onKey); root.remove(); };
}

export function attachCopyEmail(el: HTMLElement, email: string) {
  const doCopy = () => {
    const done = (ok: boolean) => {
      const t = document.createElement("div");
      t.className = "party-toast";
      t.textContent = ok ? "email copied — go say hi 📬" : "copy failed (old browser? try the mailto link)";
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 2400);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(email).then(
        () => done(true),
        () => done(false)
      );
    } else {
      const ta = document.createElement("textarea");
      ta.value = email;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); done(true); }
      catch (e) { done(false); }
      ta.remove();
    }
  };
  el.addEventListener("click", doCopy);
  return () => el.removeEventListener("click", doCopy);
}

// One hook, called once from App. All motion is scoped so it
// never fights the legacy global styles.
export function useLove() {
  useEffect(() => {
    document.documentElement.classList.add("js");
    const cleanups = [
      setupReveals(),
      setupScramble(),
      setupCountUp(),
      setupSpotlight(),
      setupMagnetic(),
      setupRouteWipe(),
      setupGrain(),
      setupCursor(),
      setupTilt(),
      setupKonami(),
      setupBurst(),
      setupHeroTerminal(),
      setupPartyKey(),
      setupHeroParallax(),
      setupScrollProgress(),
      setupScrollTop(),
      setupScrollHint(),
      setupAccentDock(),
      setupCopyCode(),
      setupNavKeys(),
      setupHelp(),
      setupLovePulse(),
    ];
    return () => {
      document.documentElement.classList.remove("js");
      cleanups.forEach((f) => f && f());
    };
  }, []);
}